<template>
  <div class="pg-page" v-loading="loading">

    <div class="pg-header">
      <div class="pg-inner pg-header-inner">
        <h1 class="pg-title">Payments</h1>
        <div class="pg-header-actions">
          <button class="pg-btn pg-btn--csv" @click="downloadCSV"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/><line x1="10" y1="9" x2="14" y2="9"/></svg> CSV</button>
        </div>
      </div>
    </div>

    <div class="pg-inner pg-list-wrap">
      <div class="pg-table">

        <div class="pg-thead">
          <div class="pg-th pg-th--txn">Transaction</div>
          <div class="pg-th pg-th--date">Date</div>
          <div class="pg-th pg-th--paytype">Pay Type</div>
          <div class="pg-th pg-th--desc">Description</div>
          <div class="pg-th pg-th--amount">Amount</div>
        </div>

        <div class="pg-tbody">
          <div
            v-for="(row, i) in tableData.data"
            :key="i"
            class="pg-row-wrap"
          >
            <div class="pg-row">
              <div class="pg-td pg-td--txn"><span class="pg-ref">{{ row.txn }}</span></div>
              <div class="pg-td pg-td--date"><span class="pg-td-muted">{{ transformDate(row.date_time) }}</span></div>
              <div class="pg-td pg-td--paytype">
                <span class="pg-pay-badge" :class="getPayClass(row.pay_type_name)">
                  <span class="pg-pay-dot"></span>{{ row.pay_type_label }}
                </span>
              </div>
              <div class="pg-td pg-td--desc"><span class="pg-td-text">{{ row.description }}</span></div>
              <div class="pg-td pg-td--amount">
                <span class="pg-amount">KES {{ row.amount.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div class="pg-empty" v-if="!loading && tableData.data.length === 0">
        <div class="pg-empty-icon">💳</div>
        <div class="pg-empty-text">No payments found</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { message } from '../../utils/message';
import api from '../../api';
import { DateTime } from 'luxon';
import { fmtDate } from '../../utils/date';

const payments = ref<any[]>([]);
const loading  = ref(true);

const loadPayments = async () => {
  try {
    const res = await api.get('customer/payments');
    const d   = res.data.data ?? {};
    // The endpoint returns the statement shape (invoices[] with embedded payments);
    // flatten non-voided payments into the flat list this view renders.
    if (Array.isArray(d.payments)) {
      payments.value = d.payments;
    } else if (Array.isArray(d.invoices)) {
      payments.value = d.invoices.flatMap((inv: any) =>
        (inv.payments ?? [])
          .filter((p: any) => Number(p.voided) !== 1)
          .map((p: any) => ({
            pay_ref:      p.pay_ref || p.reference || p.id,
            payment_date: p.payment_date,
            method:       p.method,
            reference:    p.reference,
            notes:        p.notes,
            amount:       Number(p.amount) || 0,
          })));
    } else {
      payments.value = [];
    }
  } catch {
    payments.value = [];
    message.error('Failed to load payments. Please try again.');
  } finally {
    loading.value = false;
  }
};

const tableData = computed(() => ({
  data: payments.value.map((p: any) => ({
    txn:          p.pay_ref || p.id,
    date_time:    p.payment_date,
    pay_type_name:  p.method || 'other',
    pay_type_label: (p.method || 'other').replace(/_/g, ' '),
    description:  [p.invoice_number, p.reference || p.notes].filter(Boolean).join(' · ') || '—',
    amount:       Number(p.amount) || 0,
  })),
}));

const transformDate = (d: string) => fmtDate(d);
const getPayClass   = (method: string) => {
  const norm = (method || '').toLowerCase();
  return ({
    mpesa:         'pg-pay--mpesa',
    bank_transfer: 'pg-pay--bank',
    cash:          'pg-pay--cash',
    cheque:        'pg-pay--default',
  } as Record<string, string>)[norm] ?? 'pg-pay--default';
};

const downloadCSV = () => {
  const rows = [['Transaction','Date','Method','Description','Amount']];
  tableData.value.data.forEach((r: any) => {
    rows.push([r.txn, r.date_time, r.pay_type_label, r.description, String(r.amount)]);
  });
  const esc  = (v: any) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv  = rows.map(r => r.map(esc).join(',')).join('\n');
  const link = document.createElement('a');
  link.href  = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
  link.download = `payments-${DateTime.now().toISODate()}.csv`;
  link.click();
};

onMounted(loadPayments);
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
* { box-sizing: border-box; }
.pg-page { font-family: 'DM Sans', sans-serif; background: white; min-height: 100%; }
.pg-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; }
.pg-header { background: white; padding: 24px 0 0; }
.pg-header-inner { display: flex; align-items: center; justify-content: space-between; padding-bottom: 18px; }
.pg-title { font-size: 1.3rem; font-weight: 700; color: #0061eb; margin: 0; letter-spacing: -0.01em; }
.pg-header-actions { display: flex; gap: 8px; }
.pg-btn { height: 38px; padding: 0 16px; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 0.82rem; font-weight: 600; cursor: pointer; border: none; transition: all 0.15s; white-space: nowrap; }
.pg-btn--navy { background: linear-gradient(135deg, #0061eb 0%, #003a96 100%); color: white; }
.pg-btn--navy:hover { opacity: 0.92; }
.pg-btn--csv { background: white; color: #16a34a; border: 1.5px solid #22c55e; display: inline-flex; align-items: center; gap: 6px; }
.pg-btn--csv:hover { background: #22c55e; color: white; border-color: #22c55e; }
.pg-btn--csv:active { background: #15803d; border-color: #15803d; color: white; }
.pg-list-wrap { padding-top: 4px; padding-bottom: 48px; }
.pg-table { width: 100%; border: 1.5px solid #ccdeff; border-radius: 12px; overflow: hidden; }
.pg-thead { display: grid; grid-template-columns: 150px 160px 150px 1fr 140px; background: #f0f6ff; border-bottom: 1.5px solid #ccdeff; padding: 0 20px; }
.pg-th { font-size: 0.68rem; font-weight: 700; color: #6b86b8; text-transform: uppercase; letter-spacing: 0.08em; padding: 12px 10px; white-space: nowrap; }
.pg-th--amount { text-align: right; }
.pg-tbody { display: flex; flex-direction: column; }
.pg-row-wrap { border-bottom: 1px solid #e8f0ff; }
.pg-row-wrap:last-child { border-bottom: none; }
.pg-row { display: grid; grid-template-columns: 150px 160px 150px 1fr 140px; align-items: center; padding: 0 20px; height: 56px; transition: background 0.12s; }
.pg-row:hover { background: #f0f6ff; }
.pg-td { padding: 0 10px; overflow: hidden; }
.pg-ref { font-size: 0.84rem; font-weight: 700; color: #0061eb; letter-spacing: 0.04em; text-transform: uppercase; white-space: nowrap; }
.pg-td-text { font-size: 0.83rem; color: #334155; font-weight: 400; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }
.pg-td-muted { font-size: 0.81rem; color: #64748b; font-weight: 400; white-space: nowrap; display: block; }
.pg-amount { font-size: 0.84rem; font-weight: 700; color: #0061eb; display: block; text-align: right; white-space: nowrap; }
.pg-pay-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 20px; font-size: 0.73rem; font-weight: 600; white-space: nowrap; border: 1.5px solid currentColor; text-transform: capitalize; }
.pg-pay-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
.pg-pay--mpesa   { color: #15803d; background: #f0fdf4; border-color: #86efac; }
.pg-pay--bank    { color: #2563eb; background: #eff6ff; border-color: #93c5fd; }
.pg-pay--cash    { color: #d97706; background: #fffbeb; border-color: #fcd34d; }
.pg-pay--default { color: #64748b; background: #f0f6ff; border-color: #cbd5e1; }
.pg-empty { text-align: center; padding: 60px 0; display: flex; flex-direction: column; align-items: center; gap: 12px; }
.pg-empty-icon { font-size: 2.5rem; }
.pg-empty-text { font-size: 1rem; font-weight: 500; color: #94a3b8; }
</style>